import React from 'react';
import { ChevronRightIcon, FolderPlusIcon, LayoutGridIcon, ListIcon, RefreshCwIcon, SearchIcon, UploadIcon, XIcon } from 'lucide-react';
import { FILE_TYPE_META, FILE_TYPE_ORDER } from '../../utils/fileTypes';
import type { FileType, FsFolder, Layout, SortKey, SortState, ViewId } from '../../types/files';
const VIEW_LABELS: Record<ViewId, string> = {
  files: 'Loan Documents',
  recent: 'Recent',
  starred: 'Starred',
  trash: 'Recycle Bin'
};
const SORT_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'modifiedAt:desc',
  label: 'Last modified (newest)'
}, {
  value: 'modifiedAt:asc',
  label: 'Last modified (oldest)'
}, {
  value: 'name:asc',
  label: 'Name (A–Z)'
}, {
  value: 'name:desc',
  label: 'Name (Z–A)'
}, {
  value: 'sizeKb:desc',
  label: 'Size (largest)'
}, {
  value: 'sizeKb:asc',
  label: 'Size (smallest)'
}, {
  value: 'owner:asc',
  label: 'Owner (A–Z)'
}];
interface ToolbarProps {
  view: ViewId;
  breadcrumbs: FsFolder[];
  query: string;
  activeTypes: FileType[];
  typeCounts: Map<FileType, number>;
  sort: SortState;
  layout: Layout;
  resultCount: number;
  onQueryChange: (value: string) => void;
  onToggleType: (type: FileType) => void;
  onClearFilters: () => void;
  onSortChange: (sort: SortState) => void;
  onLayoutChange: (layout: Layout) => void;
  onGoToFolder: (id: string | null) => void;
  onNewFolder: () => void;
  onUpload: () => void;
  onRefresh: () => void;
}
export function Toolbar({
  view,
  breadcrumbs,
  query,
  activeTypes,
  typeCounts,
  sort,
  layout,
  resultCount,
  onQueryChange,
  onToggleType,
  onClearFilters,
  onSortChange,
  onLayoutChange,
  onGoToFolder,
  onNewFolder,
  onUpload,
  onRefresh
}: ToolbarProps) {
  const hasFilters = activeTypes.length > 0 || query.trim().length > 0;
  const isTrash = view === 'trash';
  return <div className="border-b border-slate-200 bg-white" data-id="element-348" data-mp="3cxe299d51cri">
      <div className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-349" data-mp="381fb5e0fdgne">
        <div className="min-w-0" data-id="element-350" data-mp="16a6odz176dgb">
          {view === 'files' ? <nav aria-label="Breadcrumb" data-id="element-351" data-mp="1b1ohy20w5br9">
              <ol className="flex flex-wrap items-center gap-1 text-sm" data-id="element-352" data-mp="1b3hnfdzrppxo">
                <li data-id="element-353" data-mp="wdto2ib5d1cl">
                  <button type="button" onClick={() => onGoToFolder(null)} className="rounded font-medium text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-354" data-mp="wed7k3gg3xsq">
                    Loan Documents
                  </button>
                </li>
                {breadcrumbs.map((folder, index) => {
              const isLast = index === breadcrumbs.length - 1;
              return <li key={folder.id} className="flex items-center gap-1" data-id="element-355" data-mp="1k4plbuydgor8" data-mpi={index}>
                      <ChevronRightIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" data-id="element-356" data-mp="3mtfrv5s9bf4" />
                      {isLast ? <span aria-current="page" className="font-semibold text-slate-900" data-id="element-357" data-mp="2mtnr88lq52ek">
                          {folder.name}
                        </span> : <button type="button" onClick={() => onGoToFolder(folder.id)} className="rounded font-medium text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-358" data-mp="2ru21lxassofr">
                          {folder.name}
                        </button>}
                    </li>;
            })}
              </ol>
            </nav> : <h2 className="text-sm font-semibold text-slate-900" data-id="element-359" data-mp="1kf71rx35x06s">
              {VIEW_LABELS[view]}
            </h2>}
          <p className="mt-0.5 text-xs text-slate-500" data-id="element-360" data-mp="1gdbzap9hwgrf">
            {resultCount} {resultCount === 1 ? 'item' : 'items'}
            {query.trim().length > 0 ? ` matching “${query.trim()}”` : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-id="element-361" data-mp="2321vodm2xq4v">
          <div className="relative w-full sm:w-60" data-id="element-362" data-mp="230gzyko1415f">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-363" data-mp="27tjuow4jlgn7" />
            <input type="search" value={query} onChange={event => onQueryChange(event.target.value)} placeholder="Search all files" aria-label="Search files and folders" className="w-full rounded border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#0176d3] focus:outline-none focus:ring-1 focus:ring-[#0176d3]" data-id="element-364" data-mp="2gtfcvkwimbwq" />
          </div>

          {!isTrash && <>
              <button type="button" onClick={onNewFolder} className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm font-medium text-[#0176d3] transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-365" data-mp="ovdv3po48mp7">
                <FolderPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-366" data-mp="17sf37jcyv2xd" />
                New Folder
              </button>
              <button type="button" onClick={onUpload} className="inline-flex items-center gap-1.5 rounded bg-[#0176d3] px-2.5 py-1.5 text-sm font-semibold text-white transition hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-2" data-id="element-367" data-mp="12uwspojawfn9">
                <UploadIcon className="h-4 w-4" aria-hidden="true" data-id="element-368" data-mp="3dlwwarmm5f8o" />
                Upload
              </button>
            </>}
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-4 py-2.5 lg:flex-row lg:items-center lg:justify-between" data-id="element-369" data-mp="25kvalevl0egt">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by file type" data-id="element-370" data-mp="1vpl2qaii5cv4">
          {FILE_TYPE_ORDER.map(type => {
          const count = typeCounts.get(type) ?? 0;
          const meta = FILE_TYPE_META[type];
          const isActive = activeTypes.includes(type);
          const Icon = meta.icon;
          return <button key={type} type="button" aria-pressed={isActive} disabled={count === 0 && !isActive} onClick={() => onToggleType(type)} className={['inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-1', count === 0 && !isActive ? 'cursor-not-allowed border-slate-200 bg-white text-slate-300' : isActive ? meta.activeChipClass : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-100'].join(' ')} data-id="element-371" data-mp="1czd527xknemb">
                <Icon className={['h-3.5 w-3.5', count === 0 && !isActive ? 'text-slate-300' : meta.iconClass].join(' ')} aria-hidden="true" data-id="element-372" data-mp="17aqhvhsi7gii" />
                {meta.label}
                <span className="font-normal tabular-nums opacity-70" data-id="element-373" data-mp="1c4b5cfwh0s3g">
                  {count}
                </span>
              </button>;
        })}
          {hasFilters && <button type="button" onClick={onClearFilters} className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-374" data-mp="1m28vxa1sz0z8">
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-375" data-mp="go2qa24cleac" />
              Clear
            </button>}
        </div>

        <div className="flex items-center gap-2" data-id="element-376" data-mp="llckdv2ome4i">
          <label className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-377" data-mp="3k4j88yyg3u8z">
            <span className="hidden sm:inline" data-id="element-378" data-mp="3p2l3ei95bk0t">Sort</span>
            <select value={`${sort.key}:${sort.direction}`} onChange={event => {
            const [key, direction] = event.target.value.split(':');
            onSortChange({
              key: key as SortKey,
              direction: direction as SortState['direction']
            });
          }} className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 focus:border-[#0176d3] focus:outline-none focus:ring-1 focus:ring-[#0176d3]" data-id="element-379" data-mp="3p349n1pfojz0">
              {SORT_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-380" data-mp="1jg55tsnxpfvq">
                  {option.label}
                </option>)}
            </select>
          </label>

          <div className="flex items-center rounded border border-slate-300 bg-white" role="group" aria-label="Layout" data-id="element-381" data-mp="19gechub416x8">
            <button type="button" onClick={() => onLayoutChange('list')} aria-pressed={layout === 'list'} aria-label="List view" className={['inline-flex h-7 w-7 items-center justify-center rounded-l focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]', layout === 'list' ? 'bg-[#eef4ff] text-[#014486]' : 'text-slate-600 hover:bg-slate-100'].join(' ')} data-id="element-382" data-mp="zrwfzp9v5lze">
              <ListIcon className="h-4 w-4" aria-hidden="true" data-id="element-383" data-mp="guh4tbuhsgzl" />
            </button>
            <button type="button" onClick={() => onLayoutChange('grid')} aria-pressed={layout === 'grid'} aria-label="Grid view" className={['inline-flex h-7 w-7 items-center justify-center rounded-r border-l border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]', layout === 'grid' ? 'bg-[#eef4ff] text-[#014486]' : 'text-slate-600 hover:bg-slate-100'].join(' ')} data-id="element-384" data-mp="bvmdfliglk3v">
              <LayoutGridIcon className="h-4 w-4" aria-hidden="true" data-id="element-385" data-mp="1yhrnmsizqnl" />
            </button>
          </div>

          <button type="button" onClick={onRefresh} aria-label="Refresh" className="inline-flex h-7 w-7 items-center justify-center rounded border border-slate-300 bg-white text-slate-600 transition hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-386" data-mp="1ichmijcikq3z">
            <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-387" data-mp="1pxtu3jwbto8u" />
          </button>
        </div>
      </div>
    </div>;
}